// Game Validation Utilities
// Helper functions for game-specific validation rules

/**
 * Games that require whole number amounts (no decimals) for deposit and withdrawal
 * "Even number" here means whole number/integer (1, 2, 3... not 1.1, 1.2...)
 */
const GAMES_REQUIRING_EVEN_AMOUNT = ['pandamaster', 'firekirin'];

/**
 * Normalizes game code/name/id for comparison
 * Removes spaces, hyphens, and converts to lowercase
 */
function normalizeGameIdentifier(identifier: string | undefined | null): string {
  if (!identifier) return '';
  return identifier.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
}

/**
 * Checks if a game requires whole number amounts (no decimals)
 * @param game - Game object with id, code, or name property
 * @returns true if the game requires whole numbers (integers), false otherwise
 */
export function requiresEvenAmount(game: { id?: string; code?: string; name?: string } | null | undefined): boolean {
  if (!game) return false;

  // Check by code first (most reliable)
  if (game.code) {
    const normalizedCode = normalizeGameIdentifier(game.code);
    if (GAMES_REQUIRING_EVEN_AMOUNT.includes(normalizedCode)) {
      return true;
    }
  }

  // Check by id
  if (game.id) {
    const normalizedId = normalizeGameIdentifier(game.id);
    if (GAMES_REQUIRING_EVEN_AMOUNT.includes(normalizedId)) {
      return true;
    }
  }

  // Check by name
  if (game.name) {
    const normalizedName = normalizeGameIdentifier(game.name);
    if (GAMES_REQUIRING_EVEN_AMOUNT.includes(normalizedName)) {
      return true;
    }
  }

  return false;
}

/**
 * Validates if an amount is a whole number (no decimal places)
 * For pandamaster and firekirin, amounts must be integers (1, 2, 3... not 1.1, 1.2...)
 * @param amount - The amount to validate
 * @returns true if amount is a whole number (integer), false otherwise
 */
export function isEvenAmount(amount: number): boolean {
  return Number.isInteger(amount);
}

/**
 * Validates amount for a specific game
 * @param amount - The amount to validate
 * @param game - Game object
 * @returns Object with isValid flag and error message
 */
export function validateAmountForGame(
  amount: number,
  game: { id?: string; code?: string; name?: string } | null | undefined
): { isValid: boolean; message: string } {
  if (!game) {
    return { isValid: true, message: '' };
  }

  if (requiresEvenAmount(game)) {
    if (!isEvenAmount(amount)) {
      return {
        isValid: false,
        message: 'Amount must be a whole number (no decimals) for this game'
      };
    }
  }

  return { isValid: true, message: '' };
}

/**
 * Rounds amount to nearest whole number (integer)
 * @param amount - The amount to round
 * @returns Whole number (integer)
 */
export function roundToEven(amount: number): number {
  return Math.round(amount);
}

